import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { formatShortDate } from "../chiefComplaints/dateHelpers";
import ActivityService from "./ActivityService";
import { activityStatusBand, activityIntensityBand } from "./data";
import { ACT_V2_REFRESH } from "./events";
import ModalPortal from "../common/ModalPortal";
import MetaTooltip from "../medicines/MetaTooltip";

/**
 * "View trend" modal for one activity — same shell as the allergy /
 * addiction trend modals, columns adapted per the mock:
 *
 *   TIMESTAMP · FREQUENCY · INTENSITY · DESCRIPTION · REPORTED BY ·
 *   STATUS · flag
 *
 * Frequency renders as "N days a week" from the audit row's
 * `daysPerWeek`. Minutes per session isn't shown in the trend table
 * per the mock, but stays available in the audit row for downstream
 * consumers.
 *
 * The most-recent history row IS the live activity — flag column is
 * empty on that row and the backend rejects a flag with 400
 * (ErrCannotFlagLive). Marked-incorrect rows render struck-through
 * with a red flag; clicking the red flag opens the audit dialog
 * (BY / ON / AT — no undo, per section spec).
 */

const formatTimestamp = (iso) => {
  if (!iso) return { date: "—", time: "" };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { date: "—", time: "" };
  return {
    date: d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    time: d
      .toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase(),
  };
};

const formatTimeOnly = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();
};

const frequencyText = (r) => {
  const days = r && r.daysPerWeek;
  if (!days) return "—";
  return `${days} day${days === 1 ? "" : "s"} a week`;
};

/* Board 4122:3094 — the trend table's flag is a Material Icons `flag`
   LIGATURE at 16px, FILLED in both states: #98a2b3 on a normal row and
   #ef4444 on a marked one. It used to render as a 1.6-stroke OUTLINE when
   unmarked, which reads as a different icon beside the filled red one.
   Material Icons is not loaded in this app, so it is redrawn here as the
   equivalent inline path (same as the addictions trend modal). */
const FlagIcon = ({ marked }) => (
  <svg
    viewBox="0 0 24 24"
    fill={marked ? "#ef4444" : "#98a2b3"}
    aria-hidden="true"
  >
    <path d="M14.4 6l-.4-2H5v17h2v-7h5.6l.4 2h7V6z" />
  </svg>
);
FlagIcon.propTypes = { marked: PropTypes.bool };

/* Board 4056:3295 issues the TREND modal's close as a Material Icons `close`
   ligature at 16px / lh 16 / #6a7282 — a filled X with squared arms — where
   the three dialogs below ship the plain `×` character. Two genuinely
   different controls, so the trend head gets its own glyph. */
const MaterialCloseIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

/* Board 4059:3079 / 3083 — an 82x22 radius-4 chip. The leading dot is
   authored `hidden` on every instance, so the shape lives in CSS and only the
   band colours are inline.

   A marked row dims the WHOLE chip to 55% and recolours its label, and both
   have to be inline because the band colours are: a class rule for the label
   would lose to the inline `color` this same element carries. */
const Pill = ({ band, marked }) => (
  <span
    className="alg-trend-status"
    style={{
      background: band.bg,
      color: marked ? "#98a2b3" : band.fg,
      opacity: marked ? 0.55 : undefined,
    }}
  >
    <span
      className="alg-trend-status-dot"
      style={{ background: band.dot }}
      aria-hidden="true"
    />
    {band.label}
  </span>
);
Pill.propTypes = { band: PropTypes.object.isRequired, marked: PropTypes.bool };

// —— Review dialog ————————————————————————————————————————————————————
const ReviewEntryDialog = ({ submitting, onCancel, onConfirm }) => (
  <div className="cc-modal-backdrop delc-backdrop" onMouseDown={submitting ? null : onCancel}>
    <div
      className="alg-review"
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <header className="alg-review-head">
        <h3 className="alg-review-title">Review this entry</h3>
        <button
          type="button"
          className="alg-review-close"
          onClick={onCancel}
          aria-label="Close"
        >
          ×
        </button>
      </header>
      <p className="alg-review-body">You can mark the whole entry as incorrect.</p>
      <p className="alg-review-body">
        <strong>Please note: Marking incorrect is allowed only once.</strong>
      </p>
      <footer className="alg-review-foot">
        <button
          type="button"
          className="alg-review-mark"
          onClick={onConfirm}
          disabled={submitting}
        >
          {submitting ? "Marking…" : "Mark as incorrect"}
        </button>
      </footer>
    </div>
  </div>
);
ReviewEntryDialog.propTypes = {
  submitting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

// —— Success card ————————————————————————————————————————————————————
const SuccessDialog = ({ onDone }) => (
  <div className="cc-modal-backdrop delc-backdrop" onMouseDown={onDone}>
    <div
      className="alg-success"
      onMouseDown={(e) => e.stopPropagation()}
      role="alertdialog"
      aria-modal="true"
    >
      <button
        type="button"
        className="alg-review-close"
        onClick={onDone}
        aria-label="Close"
      >
        ×
      </button>
      <span className="alg-success-medallion" aria-hidden="true">
        <FlagIcon marked />
      </span>
      <h3 className="alg-success-title">Entry marked as incorrect</h3>
      <p className="alg-success-body">This entry remains in the audit history.</p>
      <button type="button" className="alg-success-done" onClick={onDone}>
        Done
      </button>
    </div>
  </div>
);
SuccessDialog.propTypes = { onDone: PropTypes.func.isRequired };

// —— Marked-incorrect audit ——————————————————————————————————————————
const MarkedIncorrectDialog = ({ row, onClose }) => (
  <div className="cc-modal-backdrop delc-backdrop" onMouseDown={onClose}>
    <div
      className="alg-marked"
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <header className="alg-marked-head">
        <span className="alg-marked-eyebrow">
          <FlagIcon marked /> MARKED INCORRECT
        </span>
        <button
          type="button"
          className="alg-review-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </header>
      <dl className="alg-marked-dl">
        <dt>By</dt>
        <dd>
          {row.markedIncorrectByName ||
            (row.markedIncorrectBy &&
              `${row.markedIncorrectBy.who} #${row.markedIncorrectBy.whoId}`) ||
            "—"}
        </dd>
        <dt>On</dt>
        <dd>{row.markedIncorrectAt ? formatShortDate(row.markedIncorrectAt) : "—"}</dd>
        <dt>At</dt>
        <dd>{row.markedIncorrectAt ? formatTimeOnly(row.markedIncorrectAt) : "—"}</dd>
      </dl>
    </div>
  </div>
);
MarkedIncorrectDialog.propTypes = {
  row: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

const TrendModal = ({ patientId, activityId, name, onClose }) => {
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flagTarget, setFlagTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const svc = new ActivityService();
    svc.trend(
      patientId,
      activityId,
      (res) => {
        setTrend(res || { history: [] });
        setLoading(false);
      },
      (err) => {
        setError((err && err.message) || "Failed to load trend");
        setLoading(false);
      }
    );
  }, [patientId, activityId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !submitting && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const rows = (trend && trend.history) || [];
  const liveIndex =
    rows.length > 0 ? Math.max(...rows.map((r) => r.index)) : -1;
  // Newest → oldest, matching allergy + addiction trend modals.
  const displayRows = [...rows].sort((a, b) => b.index - a.index);

  const submitMarkIncorrect = () => {
    if (!flagTarget || submitting) return;
    setSubmitting(true);
    const svc = new ActivityService();
    svc.markHistoryIncorrect(
      patientId,
      activityId,
      flagTarget.index,
      (trend && trend.version) || 0,
      () => {
        setSubmitting(false);
        setFlagTarget(null);
        setSuccessOpen(true);
        EventService.emit(ACT_V2_REFRESH);
        load();
      },
      (err) => {
        setSubmitting(false);
        setFlagTarget(null);
        // eslint-disable-next-line no-alert
        window.alert((err && err.message) || "Mark failed — please retry");
      }
    );
  };

  const flagIsMarked = flagTarget && flagTarget.markedIncorrect;

  return (
    <ModalPortal>
      <div className="cc-modal-backdrop" onMouseDown={submitting ? null : onClose}>
        <div
          className="cc-modal alg-trend pa3-trend"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="pa-trend-title"
        >
          <header className="alg-trend-head">
            <h2 id="pa-trend-title" className="alg-trend-title">
              {(name || "").toUpperCase()}
            </h2>
            <button
              type="button"
              className="alg-trend-close"
              onClick={onClose}
              aria-label="Close"
            >
              <MaterialCloseIcon />
            </button>
          </header>
  
          {/* Board 4056:3296 — the toolbar and the table sit inside ONE
              bordered, rounded, clipped card on a tinted body. They were loose
              children of the modal. */}
          <div className="pa3-trend-body">
            <div className="pa3-trend-card">
          <div className="alg-trend-sub">
            <span className="alg-trend-tab">Audit</span>
            {/* No leading middot on the board — the toolbar's 10px gap is the
                whole separation. The entry/entries switch is ours; the board
                only ever shows the plural. */}
            <span className="alg-trend-count">
              {rows.length} {rows.length === 1 ? "entry" : "entries"}
            </span>
          </div>
  
          {loading && <div className="alg-trend-empty">Loading trend…</div>}
          {error && <div className="alg-trend-empty is-error">{error}</div>}
          {!loading && !error && rows.length === 0 && (
            <div className="alg-trend-empty">No audit history yet.</div>
          )}
  
          {!loading && !error && rows.length > 0 && (
            <div className="alg-trend-table" role="table">
              <div className="alg-trend-thead" role="row">
                <span role="columnheader">Timestamp</span>
                <span role="columnheader">Frequency</span>
                <span role="columnheader">Intensity</span>
                <span role="columnheader">Description</span>
                <span role="columnheader">Reported by</span>
                <span role="columnheader">Status</span>
                <span role="columnheader" aria-label="Actions" />
              </div>
  
              {displayRows.map((r) => {
                const ts = formatTimestamp(r.recordedAt);
                const isMarked = !!r.markedIncorrect;
                const isLive = r.index === liveIndex;
                return (
                  <div
                    className={`alg-trend-row${isMarked ? " is-marked" : ""}${isLive ? " is-live" : ""}`}
                    role="row"
                    key={r.index}
                  >
                    <span className="alg-trend-cell alg-trend-ts" role="cell">
                      {/* Board 4056:3321 — an inert 12px box reserving the slot
                          the row-expand chevron would occupy. */}
                      <span className="pa3-ts-spacer" aria-hidden="true" />
                      <span className="pa3-ts-lines">
                        <span>{ts.date}</span>
                        {ts.time && <span className="alg-trend-ts-time">{ts.time}</span>}
                      </span>
                    </span>
                    <span className="alg-trend-cell" role="cell">
                      {frequencyText(r)}
                    </span>
                    <span className="alg-trend-cell" role="cell">
                      <Pill band={activityIntensityBand(r.intensity)} marked={isMarked} />
                    </span>
                    <span
                      className="alg-trend-cell alg-trend-desc"
                      role="cell"
                    >
                      {/* 3-line clamp + white hover tooltip; only reveals
                          when the text is actually being truncated. Same
                          pattern the section table uses. */}
                      <MetaTooltip text={r.description || ""} lines={3} />
                    </span>
                    <span className="alg-trend-cell" role="cell">
                      {r.reportedByName ||
                        (r.reportedBy &&
                          `${r.reportedBy.who} #${r.reportedBy.whoId}`) ||
                        "—"}
                    </span>
                    <span className="alg-trend-cell" role="cell">
                      <Pill band={activityStatusBand(r.newStatus)} marked={isMarked} />
                    </span>
                    <span className="alg-trend-cell alg-trend-flag-cell" role="cell">
                      {!isLive && (
                        <button
                          type="button"
                          className="alg-trend-flag"
                          onClick={() => setFlagTarget(r)}
                          aria-label={isMarked ? "View incorrect-mark audit" : "Review this entry"}
                          title={isMarked ? "View incorrect-mark audit" : "Review this entry"}
                        >
                          <FlagIcon marked={isMarked} />
                        </button>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
            </div>
          </div>
        </div>
  
        {flagTarget && !flagIsMarked && !successOpen && (
          <ReviewEntryDialog
            submitting={submitting}
            onCancel={() => setFlagTarget(null)}
            onConfirm={submitMarkIncorrect}
          />
        )}
        {flagTarget && flagIsMarked && (
          <MarkedIncorrectDialog
            row={flagTarget}
            onClose={() => setFlagTarget(null)}
          />
        )}
        {successOpen && <SuccessDialog onDone={() => setSuccessOpen(false)} />}
      </div>
    </ModalPortal>
  );
};

TrendModal.propTypes = {
  patientId: PropTypes.number.isRequired,
  activityId: PropTypes.string.isRequired,
  name: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default TrendModal;
